/**
 * Permission checks for UI gating. The gateway is the real boundary (403 when the Ferra
 * permission-mode is "on"); these helpers only decide what to show. Rule shared with the
 * gateway's PermissionCatalog.satisfiedBy: `write` implies `read`, `delete` is explicit.
 */
export type PermissionKey = `${string}:${'read' | 'write' | 'delete'}`;

export class PermissionSet {
  private readonly keys: ReadonlySet<string>;

  constructor(keys: Iterable<string>) {
    this.keys = new Set(keys);
  }

  /** Whether the caller satisfies one key. */
  has(key: PermissionKey): boolean {
    if (this.keys.has(key)) return true;
    if (key.endsWith(':read')) return this.keys.has(key.slice(0, -':read'.length) + ':write');
    return false;
  }

  /** Whether the caller can see a resource at all (any of read/write). */
  canView(resource: string): boolean {
    return this.has(`${resource}:read`);
  }

  /** Whether the caller can see any of the resources (none given: everyone can). */
  canViewAny(resources: string | readonly string[] | undefined): boolean {
    if (resources === undefined) return true;
    const list = typeof resources === 'string' ? [resources] : resources;
    return list.length === 0 || list.some((r) => this.canView(r));
  }

  get size(): number {
    return this.keys.size;
  }
}
